// Shared components for Frome Yoga website
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, MapPin, Clock, Users, Star } from "lucide-react";

// Logo URL
export const LOGO_URL = "https://customer-assets.emergentagent.com/job_yoga-frome/artifacts/fbti6fnl_Annelie%20Lily%20Logo%20colored%20FA%2002.png";

// Lotus Logo Component
export const LotusLogo = ({ className = "w-12 h-12" }) => (
  <img 
    src={LOGO_URL} 
    alt="Annelie Lily lotus logo with heart" 
    className={className}
    width={48}
    height={48}
    style={{ objectFit: 'contain' }}
    loading="lazy"
    decoding="async"
  />
);

// Section Label
export const SectionLabel = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
    {children}
  </span>
);

// Section Heading
export const SectionHeading = ({ children, className = "" }) => (
  <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-neutral-800 mb-5 ${className}`}>
    {children}
  </h2>
);

// Primary Button
export const ButtonPrimary = ({ href, to, children, className = "", onClick, type = "button", disabled = false, testId }) => {
  const baseClasses = "btn-primary inline-flex items-center gap-2";
  const combinedClasses = `${baseClasses} ${className}`;
  
  if (to) {
    return (
      <Link to={to} className={combinedClasses} data-testid={testId}>
        {children}
      </Link>
    );
  }
  
  if (href) {
    return (
      <a href={href} className={combinedClasses} data-testid={testId}>
        {children}
      </a>
    );
  }
  
  return (
    <button 
      type={type} 
      onClick={onClick} 
      disabled={disabled}
      className={`${combinedClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
};

// Secondary Button
export const ButtonSecondary = ({ href, to, children, className = "", testId }) => {
  const baseClasses = "btn-secondary inline-flex items-center gap-2";
  const combinedClasses = `${baseClasses} ${className}`;
  
  if (to) {
    return (
      <Link to={to} className={combinedClasses} data-testid={testId}>
        {children}
      </Link>
    );
  }
  
  return (
    <a href={href} className={combinedClasses} data-testid={testId}>
      {children}
    </a>
  );
};

// Trust Badges
export const TrustBadges = () => (
  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-neutral-600">
    <span className="flex items-center gap-2">
      <Users className="w-4 h-4 text-gold" />
      Small groups
    </span>
    <span className="flex items-center gap-2">
      <Star className="w-4 h-4 text-gold" />
      All levels welcome
    </span>
    <span className="flex items-center gap-2">
      <Clock className="w-4 h-4 text-gold" />
      Reply within 24-48h
    </span>
  </div>
);

// Contact Info Block
export const ContactInfoBlock = ({ showHeading = true }) => (
  <div className="space-y-5">
    {showHeading && (
      <div className="bg-gold/10 rounded-xl p-4 mb-6">
        <p className="text-sm text-neutral-700 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gold" />
          <strong>Reply within 24-48 hours</strong>
        </p>
      </div>
    )}
    <div className="flex items-start gap-4">
      <MapPin className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
      <div>
        <p className="text-neutral-800 font-medium">Location</p>
        <p className="text-neutral-700 text-sm">Little Keyford Lane, Frome, BA11 5BA</p>
      </div>
    </div>
    <div className="flex items-start gap-4">
      <Phone className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
      <div>
        <p className="text-neutral-800 font-medium">Phone</p>
        <a href="tel:07540995021" className="text-neutral-700 text-sm hover:text-gold transition-colors underline-offset-4 hover:underline">
          07540 995021
        </a>
      </div>
    </div>
    <div className="flex items-start gap-4">
      <Mail className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
      <div>
        <p className="text-neutral-800 font-medium">Email</p>
        <a href="mailto:seventhsensehealing@outlook.com" className="text-neutral-700 text-sm hover:text-gold transition-colors underline-offset-4 hover:underline">
          seventhsensehealing@outlook.com
        </a>
      </div>
    </div>
  </div>
);

// Page CTA Section (for bottom of pages)
export const PageCTA = ({ 
  heading = "Ready to Begin?", 
  description = "Get in touch to book your first session or ask any questions.",
  buttonText = "Enquire & Book",
  buttonHref = "/#contact"
}) => (
  <section className="py-16 px-6 md:px-12 bg-muted">
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
        {heading}
      </h2>
      <p className="text-neutral-600 mb-6">
        {description}
      </p>
      <ButtonPrimary href={buttonHref}>
        {buttonText}
        <ArrowRight className="w-4 h-4" />
      </ButtonPrimary>
    </div>
  </section>
);

// Related Services Component
export const RelatedServices = ({ services, currentSlug }) => {
  const filteredServices = services.filter(s => s.slug !== currentSlug).slice(0, 3);
  
  if (filteredServices.length === 0) return null;
  
  return (
    <section className="py-12 px-6 md:px-12 bg-cream">
      <div className="max-w-6xl mx-auto">
        <h3 className="font-heading text-2xl text-neutral-800 mb-6 text-center">You May Also Like</h3>
        <div className="grid md:grid-cols-3 gap-5">
          {filteredServices.map((service) => (
            <Link 
              key={service.slug}
              to={`/${service.slug}`}
              className="bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
            >
              <h4 className="font-heading text-lg text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                {service.title}
              </h4>
              <p className="text-neutral-600 text-sm mb-3">{service.shortDesc}</p>
              <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Areas Served Component (for location linking)
export const AreasServed = ({ highlightTown = null }) => {
  const towns = [
    { name: "Bath", slug: "yoga-bath", distance: "25 min" },
    { name: "Trowbridge", slug: "yoga-trowbridge", distance: "20 min" },
    { name: "Warminster", slug: "yoga-warminster", distance: "25 min" },
    { name: "Shepton Mallet", slug: "yoga-shepton-mallet", distance: "15 min" },
    { name: "Wells", slug: "yoga-wells", distance: "25 min" },
    { name: "Westbury", slug: "yoga-westbury", distance: "20 min" },
    { name: "Radstock", slug: "yoga-radstock", distance: "15 min" },
  ];
  
  return (
    <section className="py-12 px-6 md:px-12 bg-white/50">
      <div className="max-w-6xl mx-auto">
        <h3 className="font-heading text-2xl text-neutral-800 mb-2 text-center">Areas We Serve</h3>
        <p className="text-neutral-600 text-sm text-center mb-6">
          Based in Frome, Somerset - serving the surrounding area within 40km
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {towns.map((town) => (
            <Link 
              key={town.slug}
              to={`/${town.slug}`}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                highlightTown === town.slug 
                  ? 'bg-gold text-white' 
                  : 'bg-white/70 text-neutral-700 hover:bg-gold/10 hover:text-gold border border-gold/10'
              }`}
            >
              {town.name}
              <span className="text-xs opacity-70 ml-1">({town.distance})</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
